import React, { useContext, useEffect } from 'react';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';
import LayoutContainer from 'components/layout/LayoutContainer';
import AdminHeader from 'components/header';
import Card from 'components/inputs/Card';
import Footer from 'components/general/Footer';
import { NavContext } from 'components/privateRoute/PrivateRoute';
import { GET_FLEET_RESERVATION } from 'actions/fleetReservation';
import {
  StatusPill,
  SCOPES,
  REPEAT_TYPES,
  HISTORY_META,
  formatDate,
  formatDateTime,
  formatTime,
  formatWindow,
  describeRepeat,
} from './fleetReservationUtils';

const LEVEL = { STATION: 'Entire station', CHARGER: 'Single charger', CONNECTOR: 'Connector' };

const Field = ({ label, children, className = '' }) => (
  <div className="fr-value">
    <div className="fr-label">{label}</div>
    <div className={className}>{children || '—'}</div>
  </div>
);

Field.propTypes = { label: PropTypes.string, children: PropTypes.node, className: PropTypes.string };

const historyText = (entry) => {
  const previous = entry.previousData || {};
  const next = entry.newData || {};
  if (entry.action === 'CREATED') {
    const window = formatWindow(next.bookedStartTime, next.bookedEndTime);
    return [next.repeatType, window, next.occurrenceCount && `${next.occurrenceCount} occurrence${next.occurrenceCount === 1 ? '' : 's'}`]
      .filter(Boolean)
      .join(' · ');
  }
  const parts = [];
  const before = formatWindow(previous.bookedStartTime, previous.bookedEndTime);
  const after = formatWindow(next.timeFrom || next.bookedStartTime, next.timeTo || next.bookedEndTime);
  if (after) parts.push(before ? `${before} changed to ${after}` : `New window ${after}`);
  if (next.newDate) parts.push(`moved ${previous.reservationDate || ''} → ${next.newDate}`.trim());
  if (next.updatedRowsCount) parts.push(`${next.updatedRowsCount} row${next.updatedRowsCount === 1 ? '' : 's'} updated`);
  return parts.join(' · ');
};

const FleetReservationDetail = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { reservationCode } = useParams();
  const { navigateTo } = useContext(NavContext);
  const { detail, isDetailLoading } = useSelector((state) => state.fleetReservation);

  const isShowingOther = !detail || detail.reservationCode !== reservationCode;

  useEffect(() => {
    dispatch({ type: GET_FLEET_RESERVATION.REQUEST, payload: reservationCode });
  }, [reservationCode]);

  const renderBody = () => {
    if (isDetailLoading && isShowingOther) return <Skeleton height={200} />;
    if (isShowingOther) return <div className="empty-data-block">Reservation not found</div>;

    const { charging, occurrences } = detail;

    return (
      <>
        <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
          <h4 className="mb-0 fr-code">{detail.reservationCode}</h4>
          <StatusPill status={detail.status} />
          {detail.isActive && <span className="fr-active-dot">Active</span>}
        </div>

        <div className="fr-detail-cards">
          <div className="fr-detail-card">
            <h6>General info</h6>
            <Field label="Reservation code">
              <span className="fr-code">{detail.reservationCode}</span>
            </Field>
            <Field label="Status">
              <StatusPill status={detail.status} />
            </Field>
            <Field label="Fleet owner">{detail.fleetName}</Field>
            <Field label="Driver">{detail.memberName}</Field>
            <Field label="Created by">{detail.createdBy}</Field>
            <Field label="Created at">{formatDateTime(detail.createdAt)}</Field>
          </div>
          <div className="fr-detail-card">
            <h6>Resource</h6>
            <Field label="Station">{detail.stationName}</Field>
            <Field label="Target type">{LEVEL[detail.targetType] || detail.targetType}</Field>
            <Field label="Charger ID">
              {detail.resources.length > 0 && _.uniq(detail.resources.map((resource) => resource.chargerOcppId)).join(', ')}
            </Field>
            <Field label="Connector">
              {detail.resources.length > 0 &&
                detail.resources.map((resource) => (
                  <div key={`${resource.chargerId}-${resource.connectorId}`}>
                    {_.isNil(resource.connectorId)
                      ? `${resource.chargerOcppId}: all connectors`
                      : `${resource.chargerOcppId}: connector ${resource.connectorId}`}
                  </div>
                ))}
            </Field>
          </div>
          <div className="fr-detail-card">
            <h6>Schedule</h6>
            <Field label="Repeat type">
              {_.get(_.find(REPEAT_TYPES, { value: detail.repeatType }), 'label', detail.repeatType)}
              {detail.repeatType && detail.repeatType !== 'ONCE' && (
                <div className="fr-muted small">
                  {describeRepeat({
                    repeatType: detail.repeatType,
                    startDate: detail.seriesStartDate,
                    endDate: detail.seriesEndDate,
                    daysOfWeek: detail.daysOfWeek,
                  })}
                </div>
              )}
            </Field>
            <Field label="Date span">
              {detail.seriesStartDate && `${formatDate(detail.seriesStartDate, 'YYYY-MM-DD')} - ${formatDate(detail.seriesEndDate, 'YYYY-MM-DD')}`}
            </Field>
            <Field label="Booked window">{formatWindow(detail.bookedStartTime, detail.bookedEndTime)}</Field>
            <Field label="Buffer">
              {detail.bufferMinutes && (
                <>
                  {detail.bufferMinutes} minutes · blocked{' '}
                  <span className="fr-blocked">{formatWindow(detail.blockStartTime, detail.bookedEndTime)}</span>
                </>
              )}
            </Field>
            <Field label="Total occurrences">{detail.totalOccurrences}</Field>
          </div>
          <div className="fr-detail-card">
            <h6>Charging</h6>
            <Field label="Actual start">{formatDateTime(charging.actualStartTime)}</Field>
            <Field label="Actual end">{formatDateTime(charging.actualEndTime)}</Field>
            <Field label="Session ID">{charging.sessionId}</Field>
            <Field label="Hand-over" className="fr-blocked">
              {charging.handover && `B2C session ${charging.handover.sessionId} stopped ${formatTime(charging.handover.stoppedAt)}`}
            </Field>
          </div>
        </div>

        <Card>
          <div className="mb-2">
            <b>Occurrences</b> <span className="fr-muted small">{occurrences.length} total · one row per charger per date</span>
          </div>
          <div className="table-responsive">
            <table className="record-list-table">
              <thead>
                <tr>
                  {['Date', 'Charger ID', 'Connector', 'Time Window', 'Blocked from', 'Status', 'Actual'].map((heading) => (
                    <th key={heading}>{heading}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {occurrences.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="border-0">
                      <div className="empty-data-block">No occurrences</div>
                    </td>
                  </tr>
                ) : (
                  occurrences.map((item) => (
                    <tr key={item.id}>
                      <td>{formatDate(item.reservationDate, 'YYYY-MM-DD')}</td>
                      <td>{item.chargerOcppId}</td>
                      <td>{_.isNil(item.connectorId) ? 'All' : item.connectorId}</td>
                      <td>{formatWindow(item.bookedStartTime, item.bookedEndTime)}</td>
                      <td className="fr-blocked">{formatTime(item.blockStartTime)}</td>
                      <td>
                        <StatusPill status={item.status} />
                      </td>
                      <td>{formatWindow(item.actualStartTime, item.actualEndTime) || '—'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Card>

        <Card>
          <div className="fr-timeline">
            <b>History</b>
            {detail.history.length === 0 && <div className="fr-muted small mt-2">No history yet</div>}
            {detail.history.map((entry, index) => {
              const meta = HISTORY_META[entry.action] || { label: entry.action, dotClass: 'fr-dot--gray' };
              const text = historyText(entry);
              const scopeLabel = _.get(_.find(SCOPES, { value: entry.scopeOfChange }), 'label', '');
              const actor = _.get(entry, 'performedBy.name', entry.performedBy || '');
              return (
                <div className="fr-timeline__item" key={`${entry.action}-${entry.performedAt}-${index}`}>
                  <span className={`fr-dot ${meta.dotClass}`} />
                  <div>
                    <div>
                      <b>{meta.label}</b> {scopeLabel && <span className="fr-muted small">{scopeLabel}</span>}
                    </div>
                    {text && <div className="small">{text}</div>}
                    <div className="fr-timeline__meta">
                      {[actor, formatDateTime(entry.performedAt), entry.reason && `Reason: ${entry.reason}`].filter(Boolean).join(' · ')}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      </>
    );
  };

  return (
    <LayoutContainer>
      <AdminHeader title={t('header.fleetReservation', 'Fleet Reservation')} backTo="/fleet-reservations" />
      <SkeletonTheme baseColor="#1b2a38" highlightColor="#092235">
        <div className="page-content-wrapper scrollable fr-page">
          <div className="fr-breadcrumb">
            <span className="fr-link" onClick={() => navigateTo('/fleet-reservations')}>
              Fleet Reservation
            </span>{' '}
            / {reservationCode}
          </div>
          {renderBody()}
        </div>
      </SkeletonTheme>

      <Footer />
    </LayoutContainer>
  );
};

export default FleetReservationDetail;
